<!-- Hero Section -->
<section class="relative min-h-[90vh] flex items-center overflow-hidden">
  <!-- Background Image -->
  <div class="absolute inset-0 z-0">
    <img
      src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=800&fit=crop"
      alt="Digital Technology Background"
      class="w-full h-full object-cover"
    />
    <!-- Gradient Overlay -->
    <div class="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/85 to-primary-700/90"></div>
  </div>

  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
    <div class="grid lg:grid-cols-2 gap-12 items-center">
      <!-- Hero Content -->
      <div class="text-white">
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
          Accelerate Your
          <span class="text-primary-200 block">Digital Transformation</span>
        </h1>
        <p class="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
          From performance marketing to cloud infrastructure, we deliver comprehensive technology solutions that drive growth, optimize operations, and future-proof your business.
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <a routerLink="/contact" class="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 hover:text-primary-700 transition-all duration-300 text-center shadow-lg hover:shadow-xl">
            Get Started
          </a>
          <a routerLink="/services" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-all duration-300 text-center">
            Learn More
          </a>
        </div>
      </div>

      <!-- Hero Stats -->
      <div class="hidden lg:block">
        <div class="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <div class="grid grid-cols-2 gap-6">
            @for (stat of stats; track stat.label) {
              <div class="text-center">
                <div class="text-4xl font-bold text-white mb-2">{{ stat.value }}</div>
                <div class="text-sm text-white/80">{{ stat.label }}</div>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Wave Bottom -->
  <div class="absolute bottom-0 left-0 right-0 z-10">
    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
    </svg>
  </div>
</section>

<!-- Stats Section - Mobile -->
<section class="py-16 bg-white lg:hidden">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
      @for (stat of stats; track stat.label) {
        <div class="text-center">
          <div class="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-2">
            {{ stat.value }}
          </div>
          <div class="text-gray-600 font-medium">{{ stat.label }}</div>
        </div>
      }
    </div>
  </div>
</section>

<!-- Services Section -->
<section class="py-20 bg-gray-50" id="services">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="section-title">Our Services</h2>
      <p class="section-subtitle">
        Comprehensive solutions to power your digital success
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (service of services; track service.title) {
        <div class="card group overflow-hidden hover:-translate-y-2 transition-all duration-300">
          <!-- Service Image -->
          <div class="relative h-48 overflow-hidden">
            <img
              [src]="service.image"
              [alt]="service.title"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>

          <!-- Service Content -->
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-3">{{ service.title }}</h3>
            <p class="text-gray-600 leading-relaxed mb-4">{{ service.description }}</p>
            <a [routerLink]="service.link" class="text-primary-600 font-semibold hover:text-primary-700 transition-colors inline-flex items-center">
              Learn More
              <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      }
    </div>

    <div class="text-center mt-12">
      <a routerLink="/services" class="btn-primary inline-block">
        View All Services
      </a>
    </div>
  </div>
</section>

<!-- Why Choose Us Section -->
<section class="py-20 bg-white">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="section-title">Why Choose HoneyKode?</h2>
      <p class="section-subtitle">
        We combine expertise, innovation, and dedication to deliver exceptional results
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (item of whyChooseUs; track item.title) {
        <div class="text-center">
          <div class="w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            @switch (item.icon) {
              @case ('expertise') {
                <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                </svg>
              }
              @case ('track-record') {
                <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                </svg>
              }
              @case ('technology') {
                <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              }
              @case ('support') {
                <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/>
                </svg>
              }
            }
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-3">{{ item.title }}</h3>
          <p class="text-gray-600 leading-relaxed">{{ item.description }}</p>
        </div>
      }
    </div>
  </div>
</section>

<!-- Testimonials Section -->
<section class="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div class="text-center mb-16">
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">What Our Clients Say</h2>
      <p class="text-lg text-white/80">
        Don't just take our word for it - hear from our satisfied clients
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
      @for (testimonial of testimonials; track testimonial.author) {
        <div class="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-white hover:bg-white/15 transition-all duration-300">
          <svg class="w-10 h-10 text-white/80 mb-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
          </svg>
          <p class="text-white/90 mb-6 leading-relaxed text-sm">{{ testimonial.quote }}</p>
          <div class="flex items-center space-x-4">
            <img
              [src]="testimonial.avatar"
              [alt]="testimonial.author"
              class="w-12 h-12 rounded-full object-cover border-2 border-white/30"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex'"
            />
            <div class="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full items-center justify-center font-bold text-white hidden">
              {{ testimonial.initials }}
            </div>
            <div>
              <div class="font-semibold">{{ testimonial.author }}</div>
              <div class="text-sm text-white/70">{{ testimonial.role }}</div>
            </div>
          </div>
        </div>
      }
    </div>

  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-white">
  <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
    <h2 class="section-title">Ready to Transform Your Business?</h2>
    <p class="section-subtitle mb-8">
      Let's discuss how we can help you achieve your goals with our innovative technology solutions and expert team.
    </p>
    <div class="flex flex-col sm:flex-row justify-center gap-4">
      <a routerLink="/contact" class="btn-primary">
        Contact Us Today
      </a>
      <a routerLink="/about" class="btn-secondary">
        About Us
      </a>
    </div>
  </div>
</section>
